import React, { useCallback, useEffect, useMemo, useState } from "react";
import InputText from "../../../../components/inputs/InputText";
import {
  createPurchase,
  findPurchaseById,
  searchPurchaseSuppliers,
  updatePurchase,
} from "../../../../api/inventoryService";
import AddProductModal from "../components/AddProductModal";
import AddPurchasePaymentModal from "../components/AddPurchasePaymentModal";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import { useNavigate, useParams } from "react-router-dom";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../redux/loader/loaderActions";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaymentIcon from "@mui/icons-material/Payment";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const formatAmount = (value: number | string | undefined) => {
  const num = typeof value === "string" ? parseFloat(value) : value ?? 0;
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

function AddPurchase() {
  const [formData, setFormData] = useState<FormData>({
    supplierId: "",
    invoiceNo: "",
    invoiceDate: "",
    netTotal: 0,
    total: 0,
    lastDue: 0,
    lastPayment: 0,
    errors: false,
    product: [],
    payments: [],
  });
  const { ID } = useParams();
  const isEditMode = ID !== undefined;
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [payments, setPayments] = useState(false);
  const [supplier, setSupplier] = useState<Supplier[]>([]);
  const navigate = useNavigate();

  const productSubtotal = useMemo(
    () =>
      formData.product.reduce((sum, product) => {
        const price =
          typeof product.price === "string"
            ? parseFloat(product.price)
            : product.price;
        return sum + (Number.isFinite(price) ? price : 0);
      }, 0),
    [formData.product]
  );

  const getSupplierData = useCallback((search: string) => {
    searchPurchaseSuppliers(search)
      .then((data) => {
        setSupplier(data as Supplier[]);
      })
      .catch((error) => {
        console.error(error?.response);
      });
  }, []);

  const getPurchaseData = useCallback((purchaseId: string) => {
    findPurchaseById(purchaseId)
      .then((data) => {
        setFormData(data as FormData);
      })
      .catch((error) => {
        console.error(error?.response);
      });
  }, []);

  useEffect(() => {
    getSupplierData("");
  }, [getSupplierData]);

  useEffect(() => {
    if (ID !== undefined) {
      getPurchaseData(ID);
    }
  }, [ID, getPurchaseData]);

  const searchSupplier = useCallback(
    (e: string) => {
      getSupplierData(e);
    },
    [getSupplierData]
  );

  const handleChange = useCallback((e: any) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }, []);

  const closeAddProductModal = useCallback(() => {
    setOpenAddProduct(false);
  }, []);

  const openAddProductModal = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setOpenAddProduct(true);
    },
    []
  );

  const closePaymentModal = useCallback(() => {
    setPayments(false);
  }, []);

  const openPaymentModal = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setPayments(true);
    },
    []
  );

  const addProduct = useCallback(
    (product: Product) => {
      if (ID) {
        setFormData((prevFormData) => {
          const updatedProducts = [...prevFormData.product, product];
          const total = updatedProducts.reduce((sum, p) => {
            const price =
              typeof p.price === "string" ? parseFloat(p.price) : p.price;
            return sum + price;
          }, 0);
          const totals = prevFormData.payments.reduce((sum, payment) => {
            const price =
              (typeof payment.card === "string"
                ? parseFloat(payment.card)
                : payment.card) +
              (typeof payment.cash === "string"
                ? parseFloat(payment.cash)
                : payment.cash) +
              (typeof payment.discount === "string"
                ? parseFloat(payment.discount)
                : payment.discount);
            return sum + price;
          }, 0);

          return {
            ...prevFormData,
            product: updatedProducts,
            total: total - totals,
          };
        });
      } else {
        setFormData((prevFormData) => {
          const updatedProducts = [...prevFormData.product, product];
          const total = updatedProducts.reduce((sum, p) => {
            const price =
              typeof p.price === "string" ? parseFloat(p.price) : p.price;
            return sum + price;
          }, 0);

          return {
            ...prevFormData,
            product: updatedProducts,
            total: total,
          };
        });
      }
      closeAddProductModal();
    },
    [closeAddProductModal, ID]
  );

  const removeProductById = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
      event.preventDefault();
      setFormData((prevFormData) => {
        const updatedProducts = prevFormData.product.filter(
          (product: any) => product?.productId !== id || product.paid
        );

        const totalPrice = updatedProducts.reduce((sum, product) => {
          const price =
            typeof product.price === "string"
              ? parseFloat(product.price)
              : product.price;
          return sum + price;
        }, 0);

        return {
          ...prevFormData,
          product: updatedProducts,
          total: totalPrice,
        };
      });
    },
    []
  );

  const addPayment = useCallback(
    (payment: Payments) => {
      setFormData((prevFormData) => {
        const updatedPayments = [...prevFormData.payments, payment];
        return {
          ...prevFormData,
          payments: updatedPayments,
        };
      });
      closePaymentModal();
    },
    [closePaymentModal]
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setloadeActive();
    if (
      formData.supplierId === "" ||
      formData.invoiceNo === "" ||
      formData.invoiceDate === ""
    ) {
      setloadeDeactive();
      setFormData((prevData) => ({
        ...prevData,
        errors: true,
      }));
      return;
    }

    const request = ID
      ? updatePurchase(ID, formData)
      : createPurchase(formData);

    request
      .then(() => {
        setloadeDeactive();
        navigate("/client/inventory/purchase/all-purchase");
      })
      .catch((error) => {
        setloadeDeactive();
        console.error(error?.response);
      });
  };

  return (
    <>
      {openAddProduct && (
        <AddProductModal
          closeModal={closeAddProductModal}
          addProduct={addProduct}
        />
      )}
      {payments && (
        <AddPurchasePaymentModal
          closeModal={closePaymentModal}
          amount={formData}
          addPayment={addPayment}
        />
      )}

      <div className="space-y-3">
        {/* Page header */}
        <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <button
                type="button"
                onClick={() => navigate("/client/inventory/purchase/all-purchase")}
                className="mt-0.5 w-7 h-7 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors shrink-0"
                title="Back to purchases"
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-cyan-100 dark:bg-cyan-900/40 rounded-md flex items-center justify-center">
                    <ShoppingCartIcon className="text-cyan-600 dark:text-cyan-300" sx={{ fontSize: 14 }} />
                  </div>
                  <h1 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                    {isEditMode ? "Edit Purchase" : "Add Purchase"}
                  </h1>
                  {isEditMode && (
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                      Edit mode
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 ml-9">
                  Record supplier invoice, products, and payments
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/40 rounded-lg">
                <InventoryIcon className="text-indigo-500 dark:text-indigo-300" sx={{ fontSize: 14 }} />
                <span className="font-bold text-xs text-indigo-600 dark:text-indigo-300">
                  {formData.product.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400 text-[10px]">Products</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/40 rounded-lg">
                <PaymentIcon className="text-emerald-500 dark:text-emerald-300" sx={{ fontSize: 14 }} />
                <span className="font-bold text-xs text-emerald-600 dark:text-emerald-300">
                  {formData.payments.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400 text-[10px]">Payments</span>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Invoice details */}
          <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
            <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-3 uppercase tracking-wide">
              Invoice Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <InputDropSearch
                data={supplier}
                name="supplierId"
                label="Supplier Name"
                selectName1="supplierName"
                selectName2="suppliercompany"
                selectValue="_id"
                value={formData.supplierId}
                onChange={handleChange}
                onSearch={searchSupplier}
                isDisable={isEditMode}
                errors={formData.errors && formData.supplierId === ""}
              />
              <InputText
                name="invoiceNo"
                label="Invoice Number"
                value={formData.invoiceNo}
                onChange={handleChange}
                isDisable={isEditMode}
                errors={formData.errors && formData.invoiceNo === ""}
              />
              <InputText
                name="invoiceDate"
                label="Invoice Date"
                value={formData.invoiceDate}
                onChange={handleChange}
                type="date"
                isDisable={isEditMode}
                errors={formData.errors && formData.invoiceDate === ""}
              />
            </div>
          </div>

          {/* Products */}
          <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <InventoryIcon className="text-indigo-500 dark:text-indigo-300" sx={{ fontSize: 16 }} />
                <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200">Product List</h2>
              </div>
              <button
                type="button"
                onClick={openAddProductModal}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
              >
                <AddIcon sx={{ fontSize: 14 }} />
                Add Product
              </button>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden p-3 space-y-2">
              {formData.product.length === 0 ? (
                <div className="text-center py-6 text-slate-400 dark:text-slate-500">
                  <InventoryIcon sx={{ fontSize: 32 }} className="mb-2" />
                  <p className="text-xs">No products added yet</p>
                </div>
              ) : (
                formData.product.map((item, i) => (
                  <div
                    key={i}
                    className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 border border-slate-100 dark:border-slate-600"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="font-semibold text-xs text-slate-800 dark:text-slate-100">
                          {item?.productName}
                        </p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                          Qty: {item?.quantity} × {formatAmount(item?.unitPrice)}
                        </p>
                      </div>
                      <span className="font-bold text-xs text-emerald-600 dark:text-emerald-300">
                        {formatAmount(item?.price)}
                      </span>
                    </div>
                    {!item?.productPaid && (
                      <button
                        type="button"
                        onClick={(e) => removeProductById(e, item?.productId as unknown as number)}
                        className="w-full flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-xs font-medium"
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        Remove
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-600">
                    {productTableHeader.map((item, i) => (
                      <th
                        key={i}
                        className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider"
                      >
                        {item.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                  {formData.product.length === 0 ? (
                    <tr>
                      <td
                        colSpan={productTableHeader.length}
                        className="px-3 py-8 text-center text-slate-400 dark:text-slate-500"
                      >
                        <InventoryIcon sx={{ fontSize: 32 }} className="mb-2" />
                        <p className="text-xs">No products added yet</p>
                      </td>
                    </tr>
                  ) : (
                    formData.product.map((item, i) => (
                      <tr
                        key={i}
                        className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                            {i + 1}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-100">
                          {item?.productName}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                          {item?.quantity}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                          {formatAmount(item?.unitPrice)}
                        </td>
                        <td className="px-3 py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-300">
                          {formatAmount(item?.price)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!item?.productPaid && (
                            <button
                              type="button"
                              onClick={(e) =>
                                removeProductById(e, item?.productId as unknown as number)
                              }
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors text-xs font-medium"
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Product totals */}
            <div className="border-t border-slate-200 dark:border-slate-600 px-3 py-2 bg-slate-50/80 dark:bg-slate-700/30">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-600 p-2">
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Subtotal</p>
                  <p className="text-xs font-bold text-slate-800 dark:text-slate-100">
                    {formatAmount(productSubtotal)}
                  </p>
                </div>
                <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-600 p-2">
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Paid</p>
                  <p className="text-xs font-bold text-emerald-600 dark:text-emerald-300">
                    {formatAmount(formData.lastPayment)}
                  </p>
                </div>
                <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-600 p-2">
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Last Due</p>
                  <p className="text-xs font-bold text-amber-600 dark:text-amber-300">
                    {formatAmount(formData.lastDue)}
                  </p>
                </div>
                <div className="rounded-lg bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-600 p-2">
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">Total Payable</p>
                  <p className="text-xs font-bold text-primary dark:text-primaryLight">
                    {formatAmount(formData.total)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payments */}
          <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <PaymentIcon className="text-emerald-500 dark:text-emerald-300" sx={{ fontSize: 16 }} />
                <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200">Payment List</h2>
              </div>
              <button
                type="button"
                onClick={openPaymentModal}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:shadow-lg hover:shadow-emerald-500/20 transition-all font-medium text-xs"
              >
                <AddIcon sx={{ fontSize: 14 }} />
                Add Payment
              </button>
            </div>

            <div className="md:hidden p-3 space-y-2">
              {formData.payments.length === 0 ? (
                <div className="text-center py-6 text-slate-400 dark:text-slate-500">
                  <PaymentIcon sx={{ fontSize: 32 }} className="mb-2" />
                  <p className="text-xs">No payments recorded yet</p>
                </div>
              ) : (
                formData.payments.map((item, i) => (
                  <div
                    key={i}
                    className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 border border-slate-100 dark:border-slate-600"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                        Payment #{i + 1}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${
                          item.due > 0
                            ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                            : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                        }`}
                      >
                        Due: {formatAmount(item.due)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Total</p>
                        <p className="font-semibold text-xs text-slate-700 dark:text-slate-200">
                          {formatAmount(item.total)}
                        </p>
                      </div>
                      <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Cash</p>
                        <p className="font-semibold text-xs text-emerald-600 dark:text-emerald-300">
                          {formatAmount(item.cash)}
                        </p>
                      </div>
                      <div className="bg-white dark:bg-slate-700 rounded-md p-1.5 text-center">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Card</p>
                        <p className="font-semibold text-xs text-blue-600 dark:text-blue-300">
                          {formatAmount(item.card)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-600">
                    {paymentTableHeader.map((item, i) => (
                      <th
                        key={i}
                        className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider"
                      >
                        {item.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                  {formData.payments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={paymentTableHeader.length}
                        className="px-3 py-8 text-center text-slate-400 dark:text-slate-500"
                      >
                        <PaymentIcon sx={{ fontSize: 32 }} className="mb-2" />
                        <p className="text-xs">No payments recorded yet</p>
                      </td>
                    </tr>
                  ) : (
                    formData.payments.map((item, i) => (
                      <tr
                        key={i}
                        className="hover:bg-slate-50/50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
                            {i + 1}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-700 dark:text-slate-200">
                          {formatAmount(item.total)}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                          {formatAmount(item.discount)}
                        </td>
                        <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-100">
                          {formatAmount(item.netTotal)}
                        </td>
                        <td className="px-3 py-2 text-xs text-blue-600 dark:text-blue-300">
                          {formatAmount(item.card)}
                        </td>
                        <td className="px-3 py-2 text-xs text-emerald-600 dark:text-emerald-300">
                          {formatAmount(item.cash)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${
                              item.due > 0
                                ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                                : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                            }`}
                          >
                            {formatAmount(item.due)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer actions */}
          <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-[10px] text-slate-500 dark:text-slate-400">
              {formData.errors &&
                (formData.supplierId === "" ||
                  formData.invoiceNo === "" ||
                  formData.invoiceDate === "") && (
                  <span className="text-red-500 dark:text-red-400 font-medium">
                    Please fill in supplier, invoice number, and invoice date.
                  </span>
                )}
            </p>
            <div className="flex gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => navigate("/client/inventory/purchase/all-purchase")}
                className="px-4 py-2.5 text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors font-medium text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all font-medium text-xs"
              >
                {isEditMode ? "Update Purchase" : "Submit Purchase"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default AddPurchase;

const paymentTableHeader = [
  { title: "No" },
  { title: "Total" },
  { title: "Discount" },
  { title: "Net Total" },
  { title: "Card" },
  { title: "Cash" },
  { title: "Due" },
];

interface Product {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  price: number;
  paid: Boolean;
  productPaid?: boolean;
}

interface Payments {
  total: number;
  netTotal: number;
  discount: number;
  card: number;
  cash: number;
  due: number;
}

interface FormData {
  supplierId: string;
  invoiceNo: string;
  invoiceDate: string;
  product: Product[];
  payments: Payments[];
  netTotal: number;
  total: number;
  lastDue: number;
  lastPayment: number;
  errors: boolean;
}

interface Supplier {
  _id: string;
  supplierName: string;
  supplierCompany: string;
}

const productTableHeader = [
  { title: "No" },
  { title: "Product Name" },
  { title: "Qty" },
  { title: "Unit Price" },
  { title: "Total" },
  { title: "Action" },
];
